import React, {FC, PropsWithChildren} from 'react';
import styled, {css} from 'styled-components';

import {ClickEventHandler} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ThemeContextType} from '../../../../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

interface MessageBannerActionProps {
  /** Called whenever the button is clicked. */
  onClick?: ClickEventHandler;
  /** The color of the button text. */
  textColor?: string;
  /** The color of the hover. */
  useDarkHover?: boolean;
  /** Enable or disable the button. */
  isDisabled?: boolean;
  /** Whether to show the hover state. */
  hasHover?: boolean;
}

/*
 * Style.
 */

const StyledActionButton = styled(ChromelessButton)<MessageBannerActionProps>`
  padding: 0 8px;
  border-radius: 13px;

  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.bold};
  line-height: 26px;
  text-transform: uppercase;
  color: ${(p) => findTextColor(p)};

  ${(p) => addBackgroundStyle(p)};
`;

function findTextColor(props: MessageBannerActionProps & ThemeContextType) {
  if (props.isDisabled) {
    return props.theme.alphas.white40;
  }

  return props.textColor || props.theme.greys.white;
}

function addBackgroundStyle(props: MessageBannerActionProps) {
  if (props.isDisabled) {
    return css`
      background-color: ${(p) => p.theme.alphas.white10};
    `;
  }

  if (props.hasHover && props.useDarkHover) {
    return css`
      background-color: ${(p) => p.theme.alphas.gray20};
    `;
  }

  if (props.useDarkHover) {
    return css`
      &:hover {
        background-color: ${(p) => p.theme.alphas.gray20};
      }
    `;
  }

  if (props.hasHover) {
    return css`
      background-color: ${(p) => p.theme.alphas.white30};
    `;
  }

  return css`
    &:hover {
      background-color: ${(p) => p.theme.alphas.white30};
    }
  `;
}

/*
 * Component.
 */

export const MessageBannerAction: FC<PropsWithChildren<MessageBannerActionProps>> = (props) => (
  <StyledActionButton
    textColor={props.textColor}
    useDarkHover={props.useDarkHover}
    isDisabled={props.isDisabled}
    onClick={props.onClick}
    hasHover={props.hasHover}
  >
    {props.children}
  </StyledActionButton>
);
